import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";
import { Lock, Shield, Flag, CreditCard, Loader2, Check, Wrench } from "lucide-react";

// STRIPE INTEGRATION NOTES:
// 1. Load Stripe.js with your publishable key
// 2. Create a Stripe Elements instance
// 3. Mount the card element to #card-element
// 4. On form submit:
//    a. Validate all form fields
//    b. Call Stripe.createPaymentMethod() or Stripe.createToken()
//    c. Send payment method/token + form data to your backend
//    d. Backend creates Stripe customer + subscription with trial_period_days: 7
//    e. Backend sends webhook payload to LeadConnector API
//    f. On success response, redirect to /signup/confirmation
// 5. Display any Stripe errors in #card-errors

// WEBHOOK PAYLOAD TO LEADCONNECTOR
// Endpoint: https://services.leadconnectorhq.com/locations/ (or your GHL API endpoint)
// Method: POST
// Headers: {
//   "Authorization": "Bearer YOUR_API_KEY",
//   "Content-Type": "application/json"
// }
// Body: {
//   "companyId": "YOUR_AGENCY_COMPANY_ID",
//   "name": formData.businessName,
//   "email": formData.email,
//   "phone": formData.phone,
//   "address": "",
//   "city": "",
//   "state": "",
//   "country": "AU",
//   "postalCode": "",
//   "website": "",
//   "timezone": "Australia/Perth",
//   "snapshotId": "YOUR_TRADES_SNAPSHOT_ID",
//   "settings": {
//     "allowDuplicateContact": false,
//     "disableContactTimezone": false
//   }
// }
//
// ADDITIONAL FIELDS TO STORE (in your database or Stripe metadata):
// - ABN: formData.abn
// - Trade Type: formData.tradeType
// - Signup Date: new Date().toISOString()
// - Trial End Date: 7 days from signup

const TRADE_TYPES = [
  "Electrician",
  "Plumber",
  "HVAC",
  "Builder",
  "Renovator",
  "Painter",
  "Landscaper",
  "Roofer",
  "Concreter",
  "Fencer",
  "Other",
];

function getTrialEndDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function validateField(name, value) {
  switch (name) {
    case "firstName":
    case "lastName":
      if (!value || value.trim().length < 2)
        return name === "firstName"
          ? "First name must be at least 2 characters."
          : "Last name must be at least 2 characters.";
      return "";
    case "email":
      if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
        return "Enter a valid email address.";
      return "";
    case "phone":
      const digits = value.replace(/\s/g, "");
      if (!digits || !/^04\d{8}$/.test(digits))
        return "Enter a valid Australian mobile number (04XX XXX XXX).";
      return "";
    case "businessName":
      if (!value || value.trim().length < 2)
        return "Business name must be at least 2 characters.";
      return "";
    case "abn":
      const abnDigits = value.replace(/\s/g, "");
      if (!abnDigits || !/^\d{11}$/.test(abnDigits))
        return "Enter a valid 11-digit ABN.";
      return "";
    case "tradeType":
      if (!value) return "Select your trade type.";
      return "";
    default:
      return "";
  }
}

function FormField({
  label,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  error,
  inputMode,
  maxLength,
  required = true,
  children,
}) {
  const id = `field-${name}`;
  const errorId = `${id}-error`;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-[#1A1A1A]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children ? (
        children
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          inputMode={inputMode}
          maxLength={maxLength}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={!!error}
          data-testid={`input-${name}`}
          className={`w-full h-11 px-3 rounded-lg border text-sm text-[#1A1A1A] placeholder:text-gray-400 outline-none transition-all ${
            error
              ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-200"
              : "border-gray-300 focus:border-[#20611B] focus:ring-1 focus:ring-[#20611B]/20"
          }`}
        />
      )}
      {error && (
        <p id={errorId} className="text-xs text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default function SignupPage() {
  const navigate = useNavigate();
  const firstErrorRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [addSetup, setAddSetup] = useState(false);
  const trialEndDate = getTrialEndDate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    businessName: "",
    abn: "",
    tradeType: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = () => {
    const newErrors = {};
    let firstInvalid = null;
    for (const key of Object.keys(form)) {
      const err = validateField(key, form[key]);
      if (err) {
        newErrors[key] = err;
        if (!firstInvalid) firstInvalid = key;
      }
    }
    setErrors(newErrors);
    setTouched(
      Object.keys(form).reduce((acc, k) => ({ ...acc, [k]: true }), {})
    );
    if (firstInvalid) {
      const el = document.querySelector(`[name="${firstInvalid}"]`);
      if (el) el.focus();
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validateAll()) return;

    setIsSubmitting(true);

    // Simulated submission - replace with actual Stripe + backend call
    // In production:
    // 1. Call Stripe.createPaymentMethod() with card element
    // 2. POST payment method + form data to backend
    // 3. Backend creates customer + subscription + LeadConnector webhook
    // 4. On success, navigate to confirmation
    setTimeout(() => {
      setIsSubmitting(false);
      navigate("/signup/confirmation", {
        state: { email: form.email, trialEndDate },
      });
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="signup-page">
      {/* Minimal top bar */}
      <div
        className="border-b border-gray-100 bg-white"
        data-testid="signup-topbar"
      >
        <div className="max-w-[960px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading text-xl font-bold text-[#20611B] tracking-tight"
            data-testid="signup-logo"
          >
            {BRAND.name}
          </Link>
          <Link
            to="/"
            className="text-sm text-[#6B7280] hover:text-[#1A1A1A] transition-colors"
            data-testid="back-to-website"
          >
            Back to website
          </Link>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-[960px] mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left column: Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} noValidate>
              <h1
                className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] tracking-tight mb-2"
                data-testid="signup-heading"
              >
                Start Your 7-Day Free Trial
              </h1>
              <p className="text-[#6B7280] text-base mb-8">
                Fill in your details below. Your card will not be charged until
                day 8.
              </p>

              {/* Section 1: Your Details */}
              <div className="space-y-5 mb-8">
                {/* First + Last name side by side on desktop */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="First Name"
                    name="firstName"
                    placeholder="John"
                    value={form.firstName}
                    onChange={handleChange}
                    error={errors.firstName}
                  >
                    <input
                      id="field-firstName"
                      name="firstName"
                      type="text"
                      placeholder="John"
                      value={form.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-describedby={
                        errors.firstName ? "field-firstName-error" : undefined
                      }
                      aria-invalid={!!errors.firstName}
                      data-testid="input-firstName"
                      className={`w-full h-11 px-3 rounded-lg border text-sm text-[#1A1A1A] placeholder:text-gray-400 outline-none transition-all ${
                        errors.firstName
                          ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-200"
                          : "border-gray-300 focus:border-[#20611B] focus:ring-1 focus:ring-[#20611B]/20"
                      }`}
                    />
                  </FormField>

                  <FormField
                    label="Last Name"
                    name="lastName"
                    placeholder="Smith"
                    value={form.lastName}
                    onChange={handleChange}
                    error={errors.lastName}
                  >
                    <input
                      id="field-lastName"
                      name="lastName"
                      type="text"
                      placeholder="Smith"
                      value={form.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-describedby={
                        errors.lastName ? "field-lastName-error" : undefined
                      }
                      aria-invalid={!!errors.lastName}
                      data-testid="input-lastName"
                      className={`w-full h-11 px-3 rounded-lg border text-sm text-[#1A1A1A] placeholder:text-gray-400 outline-none transition-all ${
                        errors.lastName
                          ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-200"
                          : "border-gray-300 focus:border-[#20611B] focus:ring-1 focus:ring-[#20611B]/20"
                      }`}
                    />
                  </FormField>
                </div>

                <FormField
                  label="Email Address"
                  name="email"
                  type="email"
                  placeholder="john@example.com.au"
                  value={form.email}
                  onChange={handleChange}
                  error={errors.email}
                  inputMode="email"
                >
                  <input
                    id="field-email"
                    name="email"
                    type="email"
                    placeholder="john@example.com.au"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="email"
                    aria-describedby={
                      errors.email ? "field-email-error" : undefined
                    }
                    aria-invalid={!!errors.email}
                    data-testid="input-email"
                    className={`w-full h-11 px-3 rounded-lg border text-sm text-[#1A1A1A] placeholder:text-gray-400 outline-none transition-all ${
                      errors.email
                        ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-200"
                        : "border-gray-300 focus:border-[#20611B] focus:ring-1 focus:ring-[#20611B]/20"
                    }`}
                  />
                </FormField>

                <FormField
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  placeholder="04XX XXX XXX"
                  value={form.phone}
                  onChange={handleChange}
                  error={errors.phone}
                  inputMode="tel"
                >
                  <input
                    id="field-phone"
                    name="phone"
                    type="tel"
                    placeholder="04XX XXX XXX"
                    value={form.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="tel"
                    aria-describedby={
                      errors.phone ? "field-phone-error" : undefined
                    }
                    aria-invalid={!!errors.phone}
                    data-testid="input-phone"
                    className={`w-full h-11 px-3 rounded-lg border text-sm text-[#1A1A1A] placeholder:text-gray-400 outline-none transition-all ${
                      errors.phone
                        ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-200"
                        : "border-gray-300 focus:border-[#20611B] focus:ring-1 focus:ring-[#20611B]/20"
                    }`}
                  />
                </FormField>

                <FormField
                  label="Business Name"
                  name="businessName"
                  placeholder="Smith Electrical"
                  value={form.businessName}
                  onChange={handleChange}
                  error={errors.businessName}
                >
                  <input
                    id="field-businessName"
                    name="businessName"
                    type="text"
                    placeholder="Smith Electrical"
                    value={form.businessName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-describedby={
                      errors.businessName
                        ? "field-businessName-error"
                        : undefined
                    }
                    aria-invalid={!!errors.businessName}
                    data-testid="input-businessName"
                    className={`w-full h-11 px-3 rounded-lg border text-sm text-[#1A1A1A] placeholder:text-gray-400 outline-none transition-all ${
                      errors.businessName
                        ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-200"
                        : "border-gray-300 focus:border-[#20611B] focus:ring-1 focus:ring-[#20611B]/20"
                    }`}
                  />
                </FormField>

                <FormField
                  label="ABN"
                  name="abn"
                  placeholder="XX XXX XXX XXX"
                  value={form.abn}
                  onChange={handleChange}
                  error={errors.abn}
                  maxLength={14}
                >
                  <input
                    id="field-abn"
                    name="abn"
                    type="text"
                    placeholder="XX XXX XXX XXX"
                    value={form.abn}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={14}
                    aria-describedby={
                      errors.abn ? "field-abn-error" : undefined
                    }
                    aria-invalid={!!errors.abn}
                    data-testid="input-abn"
                    className={`w-full h-11 px-3 rounded-lg border text-sm text-[#1A1A1A] placeholder:text-gray-400 outline-none transition-all ${
                      errors.abn
                        ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-200"
                        : "border-gray-300 focus:border-[#20611B] focus:ring-1 focus:ring-[#20611B]/20"
                    }`}
                  />
                </FormField>

                <FormField
                  label="Trade Type"
                  name="tradeType"
                  value={form.tradeType}
                  onChange={handleChange}
                  error={errors.tradeType}
                >
                  <select
                    id="field-tradeType"
                    name="tradeType"
                    value={form.tradeType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-describedby={
                      errors.tradeType ? "field-tradeType-error" : undefined
                    }
                    aria-invalid={!!errors.tradeType}
                    data-testid="input-tradeType"
                    className={`w-full h-11 px-3 rounded-lg border text-sm outline-none transition-all appearance-none bg-white ${
                      !form.tradeType ? "text-gray-400" : "text-[#1A1A1A]"
                    } ${
                      errors.tradeType
                        ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-200"
                        : "border-gray-300 focus:border-[#20611B] focus:ring-1 focus:ring-[#20611B]/20"
                    }`}
                  >
                    <option value="" disabled>
                      Select your trade
                    </option>
                    {TRADE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>

              {/* Section 2: Payment Details */}
              <div className="mb-8">
                <h2 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4">
                  Payment Details
                </h2>

                {/* Stripe Elements mounts here. Initialize with Stripe.js and your publishable key. */}
                <div
                  id="card-element"
                  data-testid="stripe-card-element"
                  className="w-full h-11 px-3 py-3 rounded-lg border border-gray-300 bg-white flex items-center"
                >
                  <span className="text-sm text-gray-400">
                    Card details (Stripe Elements will mount here)
                  </span>
                </div>
                <div
                  id="card-errors"
                  data-testid="stripe-card-errors"
                  className="mt-1.5 text-xs text-red-500"
                  role="alert"
                />

                <div className="flex items-center gap-2 mt-3">
                  <Lock className="h-3.5 w-3.5 text-[#6B7280]" />
                  <span className="text-xs text-[#6B7280]">
                    Payments processed securely by Stripe. Your card details
                    never touch our servers.
                  </span>
                </div>
              </div>

              {/* Submit error */}
              {submitError && (
                <div
                  className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                  data-testid="submit-error"
                  role="alert"
                >
                  {submitError}
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                data-testid="signup-submit"
                className="w-full bg-[#D4A84B] hover:bg-[#B88E3B] text-white font-bold h-14 rounded-md shadow-md hover:shadow-lg transition-all active:scale-95 text-base tracking-wide disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  "Start My Free Trial"
                )}
              </Button>

              <p className="text-xs text-[#6B7280] text-center mt-3">
                By signing up, you agree to our{" "}
                <Link
                  to="/terms"
                  className="text-[#20611B] underline hover:no-underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  className="text-[#20611B] underline hover:no-underline"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </form>
          </div>

          {/* Right column: Order Summary */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-8">
              <div
                data-testid="order-summary"
                className="bg-[#F9FAFB] border border-gray-200 rounded-xl p-6 md:p-8"
              >
                <h2 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-5">
                  Order Summary
                </h2>

                {/* Line items */}
                <div className="space-y-3 mb-5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#1A1A1A]">
                      Epsillon Leads Platform
                    </span>
                    <span className="text-sm text-[#1A1A1A] font-medium">
                      $299/month
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#1A1A1A]">
                      7-Day Free Trial
                    </span>
                    <span
                      className="text-sm font-medium text-[#20611B]"
                      data-testid="trial-price"
                    >
                      $0.00 today
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 my-4" />

                {/* Total */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-base font-bold text-[#1A1A1A]">
                    Due today
                  </span>
                  <span
                    className="text-lg font-bold text-[#20611B]"
                    data-testid="due-today"
                  >
                    $0.00
                  </span>
                </div>

                <p
                  className="text-xs text-[#6B7280] leading-relaxed"
                  data-testid="payment-date-note"
                >
                  Your first payment of $299 AUD (inc GST) will be charged on{" "}
                  <span className="font-medium">{trialEndDate}</span>. SMS and
                  call usage is billed separately based on what you use.
                </p>

                {/* Professional Setup upsell */}
                <div className="border-t border-gray-200 mt-5 pt-5">
                  <button
                    type="button"
                    onClick={() => setAddSetup(!addSetup)}
                    data-testid="setup-upsell-toggle"
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      addSetup
                        ? "border-[#20611B] bg-[#D8FFB8]/15"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                        addSetup
                          ? "bg-[#20611B] border-[#20611B]"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {addSetup && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-sm font-semibold text-[#1A1A1A]">
                          Professional Setup
                        </span>
                        <span className="text-sm font-bold text-[#1A1A1A] ml-2">
                          $499
                        </span>
                      </div>
                      <p className="text-xs text-[#6B7280] leading-relaxed">
                        We configure your pipelines, automations, templates, and integrations. Done in 48 hours.
                      </p>
                    </div>
                  </button>

                  {addSetup && (
                    <div
                      className="mt-3 space-y-1.5 pl-8"
                      data-testid="setup-upsell-details"
                    >
                      {[
                        "Custom pipeline stages",
                        "SMS and email templates",
                        "Google Calendar integration",
                        "Review automation setup",
                        "Lead capture form built",
                        "30-minute walkthrough call",
                      ].map((item) => (
                        <div key={item} className="flex items-center gap-2">
                          <Check className="h-3 w-3 text-[#20611B]" />
                          <span className="text-xs text-[#6B7280]">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Trust signals */}
              <div
                className="mt-6 space-y-3"
                data-testid="signup-trust-signals"
              >
                {[
                  {
                    icon: Lock,
                    text: "256-bit SSL encrypted",
                  },
                  {
                    icon: Shield,
                    text: "Cancel anytime. No lock-in.",
                  },
                  {
                    icon: Flag,
                    text: "Australian owned and operated",
                  },
                  {
                    icon: CreditCard,
                    text: "Powered by Stripe",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2.5"
                    data-testid={`trust-signal-${i}`}
                  >
                    <item.icon className="h-4 w-4 text-[#20611B]/60" />
                    <span className="text-xs text-[#6B7280]">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
